import { useState, useEffect, useRef } from 'react'
import { useNotifications } from '../context/NotificationContext'
// Toast import removed — system status no longer fires toasts
import { useAuth } from '../context/AuthContext'
import { API_BASE } from '../utils/api'

type IndStatus = 'live' | 'connected' | 'running' | 'active' | 'open'
  | 'delayed' | 'idle' | 'pre-open'
  | 'disconnected' | 'stopped' | 'error' | 'closed'

interface Indicator {
  key: string
  label: string
  status: IndStatus
  detail?: string
}

const STATUS_COLOR: Record<string, string> = {
  live: '#8ab82a', connected: '#8ab82a', running: '#8ab82a',
  active: '#8ab82a', open: '#8ab82a',
  delayed: '#eab308', idle: '#eab308', 'pre-open': '#eab308',
  disconnected: '#d4495a', stopped: '#d4495a', error: '#d4495a',
  closed: '#64748b',
}

const STATUS_LABEL: Record<string, string> = {
  live: 'LIVE', connected: 'OK', running: 'RUN', active: 'OK', open: 'OPEN',
  delayed: 'LAG', idle: 'IDLE', 'pre-open': 'PRE',
  disconnected: 'DOWN', stopped: 'OFF', error: 'ERR', closed: 'CLOSED',
}

const isPulse = (s: IndStatus) =>
  ['live', 'connected', 'running', 'active', 'open'].includes(s)

const isCritical = (s: IndStatus) =>
  ['disconnected', 'stopped', 'error'].includes(s)

const isWarn = (s: IndStatus) =>
  ['delayed', 'idle', 'pre-open'].includes(s)

function getMarketStatus(): IndStatus {
  const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }))
  const mins = ist.getHours() * 60 + ist.getMinutes()
  if (mins >= 9 * 60 + 15 && mins <= 15 * 60 + 30) return 'open'
  if (mins >= 9 * 60 && mins < 9 * 60 + 15) return 'pre-open'
  return 'closed'
}

const VPS_HEALTH_URL = 'https://tunnel.la-money.co.in/health'

// ── Single row ────────────────────────────────────────────────
function IndicatorRow({ ind }: { ind: Indicator }) {
  const color = STATUS_COLOR[ind.status] ?? '#64748b'
  const badge = STATUS_LABEL[ind.status] ?? ind.status.toUpperCase()
  const pulse = isPulse(ind.status)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0' }}>
      <span style={{
        flexShrink: 0, width: 7, height: 7, borderRadius: '50%',
        background: color,
        boxShadow: pulse ? `0 0 5px ${color}` : 'none',
        animation: pulse ? 'pulse-dot 2s ease-in-out infinite' : 'none',
      }} />
      <span style={{
        flex: 1, fontSize: '0.6875rem', fontWeight: 600,
        color: 'var(--text-secondary)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {ind.label}
        {ind.detail && (
          <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: '0.25rem' }}>
            {ind.detail}
          </span>
        )}
      </span>
      <span style={{
        flexShrink: 0, fontSize: '0.5625rem', fontWeight: 800,
        letterSpacing: '0.05em', color,
        background: `${color}18`, border: `1px solid ${color}44`,
        borderRadius: '3px', padding: '0.1rem 0.35rem',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        {badge}
      </span>
    </div>
  )
}

// ── System Status Panel ───────────────────────────────────────
export function SystemStatus() {
  const { addNotification } = useNotifications()
  // Toast removed — sidebar panel already shows system status inline
  const { token, isAdmin, session } = useAuth()

  const [indicators, setIndicators] = useState<Indicator[]>([
    { key: 'market_h', label: 'Market Hours', status: getMarketStatus() },
    { key: 'engine',   label: 'Trading Engine', status: 'stopped', detail: 'VPS' },
    { key: 'api',      label: 'API Worker',     status: 'stopped', detail: 'Cloudflare' },
  ])

  // Track previous statuses to detect changes
  const prevRef = useRef<Record<string, IndStatus>>({})

  // Fetch real statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      const newIndicators: Indicator[] = [
        { key: 'market_h', label: 'Market Hours', status: getMarketStatus() },
      ]

      // Check VPS Trading Engine health
      try {
        const vpsRes = await fetch(VPS_HEALTH_URL, { signal: AbortSignal.timeout(5000) })
        if (vpsRes.ok) {
          const vpsData = await vpsRes.json()
          newIndicators.push({
            key: 'engine',
            label: 'Trading Engine',
            status: 'running',
            detail: `VPS • ${Math.round(vpsData.uptime)}s up`,
          })
        } else {
          newIndicators.push({ key: 'engine', label: 'Trading Engine', status: 'error', detail: 'VPS' })
        }
      } catch {
        newIndicators.push({ key: 'engine', label: 'Trading Engine', status: 'disconnected', detail: 'VPS Offline' })
      }

      // Check Cloudflare Worker API health
      try {
        const apiRes = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(5000) })
        if (apiRes.ok) {
          newIndicators.push({ key: 'api', label: 'API Worker', status: 'running', detail: 'Cloudflare' })
        } else {
          newIndicators.push({ key: 'api', label: 'API Worker', status: 'error', detail: 'Cloudflare' })
        }
      } catch {
        newIndicators.push({ key: 'api', label: 'API Worker', status: 'disconnected', detail: 'Offline' })
      }

      // Fetch broker firms from CF Worker DB.
      // WebSocket (IRIS/Apollo) is ONE shared connection per broker firm — not per client.
      // Show one status row per firm indicating how many clients are logged in.
      try {
        const brokersRes = await fetch(`${API_BASE}/api/brokers`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          signal: AbortSignal.timeout(5000),
        })
        if (brokersRes.ok) {
          const brokersData = await brokersRes.json()
          if (Array.isArray(brokersData)) {
            brokersData.forEach((b: any) => {
              const clients: any[] = b.clients || []
              const connectedCount = clients.filter((c: any) => c.status === 'connected').length
              const total = clients.length
              const firmStatus: IndStatus = connectedCount > 0 ? 'connected'
                : clients.some((c: any) => c.status === 'connecting') ? 'delayed'
                : total === 0 ? 'stopped'
                : 'disconnected'
              newIndicators.push({
                key: `broker-${b.id}`,
                label: b.short_name || b.name,
                status: firmStatus,
                detail: total === 0 ? 'no clients' : `${connectedCount}/${total} clients`,
              })
            })
          }
        }
      } catch { /* silent */ }

      // ─── WebSocket composite status ─────────────────────────
      // Fetch IRIS/Apollo WS status from VPS via API proxy (avoids CORS)
      try {
        const connectedClients = newIndicators.filter(i => i.key.startsWith('broker-') && (i.status === 'connected' || i.status === 'delayed'))
        if (connectedClients.length === 0) {
          newIndicators.push({ key: 'ws', label: 'WebSocket', status: 'stopped', detail: 'no clients' })
        } else {
          const vpsStatus = await fetch(`${API_BASE}/api/vps/brokers`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            signal: AbortSignal.timeout(4000),
          })
          if (vpsStatus.ok) {
            const vpsBrokers: any[] = await vpsStatus.json()
            if (Array.isArray(vpsBrokers)) {
              const irisUp = vpsBrokers.some(b => b.irisConnected)
              const apolloUp = vpsBrokers.some(b => b.apolloConnected)
              const wsStatus: IndStatus = irisUp && apolloUp ? 'connected'
                : irisUp || apolloUp ? 'delayed'
                : 'disconnected'
              newIndicators.push({
                key: 'ws',
                label: 'WebSocket',
                status: wsStatus,
                detail: `IRIS ${irisUp ? '✓' : '✗'} · Apollo ${apolloUp ? '✓' : '✗'}`,
              })
            }
          } else {
            newIndicators.push({ key: 'ws', label: 'WebSocket', status: 'error', detail: 'VPS unreachable' })
          }
        }
      } catch {
        newIndicators.push({ key: 'ws', label: 'WebSocket', status: 'error', detail: 'VPS unreachable' })
      }

      setIndicators(_prev => {
        // Detect status changes and fire notifications
        newIndicators.forEach(ind => {
          const old = prevRef.current[ind.key]
          if (old !== undefined && old !== ind.status) {
            if (isCritical(ind.status)) {
              addNotification(
                `⚠ ${ind.label} — ${STATUS_LABEL[ind.status]}`,
                `${ind.label} is now ${ind.status.toUpperCase()}.`,
                'system'
              )

            } else if (isPulse(ind.status) && (isCritical(old) || isWarn(old))) {
              addNotification(
                `✅ ${ind.label} — Recovered`,
                `${ind.label} is back to ${STATUS_LABEL[ind.status]}.`,
                'system'
              )

            }
          }
          prevRef.current[ind.key] = ind.status
        })
        return newIndicators
      })
    }

    fetchStatuses()
    const id = setInterval(fetchStatuses, 12000)
    return () => clearInterval(id)
  }, [token, addNotification])

  const criticalCount = indicators.filter(i => isCritical(i.status)).length
  const warnCount     = indicators.filter(i => isWarn(i.status)).length
  const overallColor  = criticalCount > 0 ? '#d4495a' : warnCount > 0 ? '#eab308' : '#8ab82a'
  const overallLabel  = criticalCount > 0 ? `${criticalCount} DOWN` : warnCount > 0 ? 'WARN' : 'ALL OK'

  return (
    <div style={{
      margin: '0.5rem 0.625rem 0',
      padding: '0.625rem 0.75rem',
      background: 'rgba(14,26,50,0.55)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <span style={{
          fontSize: '0.625rem', fontWeight: 800,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}>
          System Status
        </span>
        <span style={{
          fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.06em',
          color: overallColor, background: `${overallColor}18`,
          border: `1px solid ${overallColor}44`,
          borderRadius: '3px', padding: '0.1rem 0.4rem',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {overallLabel}
        </span>
      </div>

      {/* Indicators */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {indicators.map(ind => <IndicatorRow key={ind.key} ind={ind} />)}
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '0.5rem', paddingTop: '0.375rem',
        borderTop: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
          ⟳ 12s heartbeat
        </span>
        <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
          {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST
        </span>
      </div>
    </div>
  )
}
